<template>
  <div>
    <h1>Privileged Users</h1>
    <div>
      <create-privileged-user class="panel"
        :roles="sortedAndFilteredRoles"
        @loading="(val) => loading = val"
        @user-created="getPrivilegedUsers"
      ></create-privileged-user>
      <k-panel v-if="rolesReady"
               :showContent="showRoleDescriptions"
               @toggle="showRoleDescriptions = !showRoleDescriptions"
               :title="'Roles'"
               :icon="'fa-info-circle'"
               :duotone="false"
      >
        <template #body>
          <ul class="roles">
            <li v-for="(role, idx) in sortedAndFilteredRoles" :key="idx">
              <p><span>{{ role.name.replaceAll('_', ' ') }}</span> - {{ role.description }}</p>
            </li>
          </ul>
        </template>
      </k-panel>
      <k-table
        :rows="kableRows"
        :headers="kableHeaders"
        :hideable="false"
        :max="10"
        @clicked="tableCallback"
      ></k-table>
      <update-privileged-user
        :privilegedUser="privilegedUserToUpdate"
        :roles="sortedAndFilteredRoles"
        :programmes="programmes"
        :employers="employers"
        @close="closeEditModal"
        @privileged-user-updated="getPrivilegedUsers"
        ref="editModal"
      ></update-privileged-user>
    </div>
  </div>
</template>

<style>
ul.roles {
  list-style: none;
  padding-left: 0;
}

ul.roles li > p > span {
  font-weight: bold;
  color: var(--kate-type-accent);
}

[data-theme="legacy"] ul.roles li > p > span {
  color: var(--kate-primary);
}

.k-table-container .k-table-header-is_data_mentor,
.k-table-container .k-table-key-is_data_mentor,
.k-table-container .k-table-header-is_coach,
.k-table-container .k-table-key-is_coach {
  text-align: center;
}

th.k-table-header.k-table-header-edit,
.k-table-container th {
  min-width: unset;
}

.k-table-container i.fas {
  font-size: 1em;
}

.k-table-container i.fas.fa-check-circle {
  color: var(--kate-primary);
}
</style>

<script>
import KTable from '@base-components/tables/display/k-table.vue';
import PageReadyMixin from '@mixins/page-ready-mixin';
import KPanel from '@base-components/k-panel.vue';
import { formatId } from '@utils/format-user-id';
import { sortObjectArray } from '@utils/sort-by-object-property';
import UpdatePrivilegedUser from './update-privileged-user.vue';
import CreatePrivilegedUser from './create-privileged-user.vue';

export default {
  components: {
    'create-privileged-user': CreatePrivilegedUser,
    'update-privileged-user': UpdatePrivilegedUser,
    'k-table': KTable,
    'k-panel': KPanel,
  },

  mixins: [PageReadyMixin],

  data() {
    return {
      roles: [],
      rolesReady: false,
      programmes: [],
      programmesReady: false,
      employers: [],
      employersReady: false,
      privilegedUsers: [],
      privilegedUsersReady: false,
      privilegedUserId: undefined,
      loading: false,
      permissionsUpdated: false,
      showRoleDescriptions: false,
    };
  },

  beforeMount() {
    this.$Loading.start();
    this.getRoles();
    this.getProgrammes();
    this.getPrivilegedUsers();
    this.getEmployers();
  },

  computed: {
    ready() {
      return this.rolesReady && this.programmesReady && this.privilegedUsersReady && this.employersReady && !this.loading;
    },
    privilegedUserToUpdate() {
      if (this.privilegedUserId) {
        return this.privilegedUsers.find(x => x.id === this.privilegedUserId);
      }
      return {};
    },
    kableHeaders() {
      return {
        edit: {
          name: 'Edit',
          type: 'action',
        },
        name: {
          name: 'User',
          sortable: true,
          filterable: true,
          type: 'url',
        },
        email: {
          name: 'Email',
          sortable: true,
          filterable: true,
        },
        roles: {
          name: 'Roles',
          type: 'tags',
          filterable: true,
          sortable: true,
        },
        learnerSupportRole: {
          name: 'Learner Support Role',
          type: 'pill',
          sortable: false,
          filterable: false,
          options: {
            type: 'neutral',
          },
        },
      };
    },
    kableRows() {
      return this.privilegedUsers.map((x, index) => ({
        id: x.id,
        edit: {
          key: index,
          text: '<i class="fas fa-edit" aria-hidden="true"></i>',
        },
        name: {
          text: `${x.full_name}<br><span class="user-id">${formatId(x.id)}</span>`,
          path: {
            name: 'dashboard_students',
            params: {
              studentId: x.id,
            },
          },
        },
        email: x.email,
        roles: {
          tags: x.roles ? x.roles.map(r => r.role.replaceAll('_', ' ')).sort() : [],
          sortBy: x.roles ? [...x.roles].map(r => r.role).sort().join(' ') : '',
        },
        learnerSupportRole: this.learnerSupportLabel(x),
      }));
    },
    sortedAndFilteredRoles() {
      const roles = this.roles ? sortObjectArray(this.roles.map(x => ({ ...x, numberOfPermissions: x.permissions.length })), 'numberOfPermissions') : undefined;
      if (roles && !this.$permissions.hasPermission('assign_admin_role')) {
        return roles.filter(r => r.name !== 'admin');
      }
      return roles;
    },
  },

  methods: {

    getRoles() {
      this.rolesReady = false;
      this.$logger.info('Getting roles');
      this.$http.get('/api/profile/roles').then(res => {
        this.$logger.info('Successfully retrieved roles');
        this.roles = res.data;
      }).catch(err => {
        this.$logger.autowarn('There was an error while attempting to retrieve roles', undefined, err);
        this.$ktoast.showError(err);
      }).then(() => {
        this.rolesReady = true;
      });
    },
    getProgrammes() {
      this.programmesReady = false;
      this.$logger.info('Getting programmes for privileged users');
      return this.$http.get('/api/curriculum/programmes?dashboard=true')
        .then(result => {
          this.$logger.info('Got programmes for privileged user');
          this.programmes = result.data.programmes;
        }).catch(err => {
          this.$logger.autowarn('Error getting programmes for privileged users', undefined, err);
          this.$ktoast.showError(err);
        }).then(() => {
          this.programmesReady = true;
        });
    },
    getPrivilegedUsers() {
      this.privilegedUsersReady = false;
      this.$logger.info('Getting privileged users');
      this.$http.get('/api/profile/users/privileged').then(res => {
        this.$logger.info('Successfully retrieved privileged users');
        this.privilegedUsers = res.data.users;
      }).catch(err => {
        this.$logger.autowarn('There was an error while attempting to retrieve privileged users', undefined, err);
        this.$ktoast.showError(err);
      }).then(() => {
        if (this.permissionsUpdated) {
          this.$ktoast.success('Permissions for user updated.');
        }
        this.privilegedUsersReady = true;
      });
    },
    getEmployers() {
      this.employersReady = false;
      this.$logger.info('Getting employers');
      this.$http.get('/api/profile/employers').then(res => {
        this.$logger.info('Successfully retrieved employers list');
        this.employers = res.data;
      }).catch(err => {
        this.$logger.autowarn('There was an error while attempting to retrieve employers list in privileged users', undefined, err);
        this.$ktoast.showError(err);
      }).then(() => {
        this.employersReady = true;
      });
    },
    tableCallback(key, index, row, column) {
      if (column === 'edit') {
        this.privilegedUserId = this.privilegedUsers.find(x => x.id === row.id).id;
      }
    },
    closeEditModal() {
      this.privilegedUserId = undefined;
      this.$refs.editModal.reset();
    },
    getIcon(val, additionalContent) {
      let html;
      if (val) {
        html = '<i class="fas fa-check-circle" aria-hidden="true"></i>';
      } else {
        html = '<i class="fas fa-minus" aria-hidden="true"></i>';
      }
      if (additionalContent) {
        html += `<br>${additionalContent}`;
      }
      return html;
    },
    learnerSupportLabel(user) {
      if (user.is_coach) {
        return 'Coach';
      }
      if (user.is_data_mentor) {
        return 'Data Mentor';
      }
      return '-';
    },
  },
};
</script>
